import type { Message } from "../types";
import { describeCard } from "./gbpPlan";

/**
 * A text stand-in for an assistant turn whose content is a card, not prose.
 *
 * Cards are attached to a message with `content: ""`, so the outgoing request
 * built them as `content: []`. Bedrock rejects that outright — "messages: text
 * content blocks must be non-empty" — which takes down the next chat turn after
 * any card, not the turn that produced it.
 *
 * Beyond keeping the request valid, the stand-in is what lets the model answer a
 * follow-up about the card on screen: "we met in georgetown, greet them" only
 * makes sense to it if the scanned contacts are in the transcript at all. The
 * backend already does exactly this for STORED rows
 * (`chat.py::_synthesize_history_text`); this is the in-memory counterpart, and
 * the wording is kept close to it on purpose.
 *
 * Returns "" when the message carries nothing at all — the caller drops those,
 * matching the backend's own `if not text: continue`.
 *
 * `cardRecency` is only meaningful on the two card turn types, and only when the
 * conversation holds more than one of them: "superseded" on the older scans,
 * "latest" on the current one. Mirrors chat.py's `card_recency`.
 */
export const assistantStandInText = (
  msg: Message,
  cardRecency: "" | "latest" | "superseded" = ""
): string => {
  if (msg.content && msg.content.trim()) return msg.content.trim();

  // Names, not just counts. "1 scanned contact(s)" reads as no information at all:
  // asked "who did I just scan?", the model correctly answered that no name had
  // reached it (live, 2026-09-25). Whoever is on screen has to be in the text.
  //
  // The address and number belong here too. Without them the model answered "the
  // business card scan only gave me his name and company — nothing to send to yet"
  // and then refused to believe the owner who could see the email on screen
  // (live, 2026-09-25). It was quoting what it had been handed, exactly.
  const named = (
    people: {
      name: string | null;
      company?: string | null;
      email?: string | null;
      phone?: string | null;
      mobile?: string | null;
    }[]
  ) =>
    people
      .map((c) => {
        const who = [c.name, c.company].filter(Boolean).join(" of ") || "an unnamed contact";
        const reach = [c.email, c.phone || c.mobile].filter(Boolean).join(", ");
        return `${who}${reach ? ` (${reach})` : " (no email or phone on the card)"}`;
      })
      .join("; ");

  // An older scan in the same conversation still claimed to be the one on screen, so
  // two scans read as two current cards and the model anchored on the first (live,
  // 2026-09-25: the owner scanned a second card and kept getting the first one's
  // details back). Order alone did not convey it; saying so does.
  const superseded = (contacts: Parameters<typeof named>[0]) =>
    `[An earlier scan in this conversation, since replaced: ${named(contacts)}. ` +
    `The owner has scanned again since — use the most recent scan further down and ` +
    `ignore these details.]`;

  const lead =
    cardRecency === "latest" ? "The current scan, most recent in this conversation: " : "Scanned ";

  if (msg.cardDetails) {
    const { contacts } = msg.cardDetails;
    if (cardRecency === "superseded") return superseded(contacts);
    return (
      `[${lead}${contacts.length} business card(s): ${named(contacts)}. ` +
      `These are the details the scan read and they are on the owner's screen to ` +
      `correct — you already have them, so do not ask for an email address or phone ` +
      `number that appears above. No intro has been written yet.]`
    );
  }
  if (msg.cardContactsPending) {
    return `[Writing intro emails for ${msg.cardContactsPending.count} scanned card(s).]`;
  }
  if (msg.cardContacts) {
    const { contacts } = msg.cardContacts;
    if (cardRecency === "superseded") return superseded(contacts);
    const subjects = contacts
      .filter((c) => c.draft)
      .map((c) => `to ${c.name || "an unnamed contact"}, subject "${c.draft!.subject}"`)
      .join("; ");
    return (
      `[${lead}${contacts.length} business card(s): ${named(contacts)}. ` +
      `Drafted intro emails ${subjects || "(none yet)"}. ` +
      `The drafts are on screen awaiting the owner's approval — nothing is saved to the CRM and no email has been sent.]`
    );
  }
  if (msg.calls) {
    const n = msg.calls.calls?.length ?? 0;
    return `[Delivered ${n} call(s).]`;
  }
  if (msg.contactBatch) {
    const n = msg.contactBatch.contacts?.length ?? 0;
    return `[Delivered ${n} contact(s) for review before import.]`;
  }
  if (msg.blogPost) {
    const title = (msg.blogPost.title || "").trim();
    return `[Delivered blog post${title ? `: ${title}` : ""}.]`;
  }
  if (msg.outreachEmail) {
    const subject = (msg.outreachEmail.subject || "").trim();
    return `[Delivered outreach email draft${subject ? `: ${subject}` : ""}.]`;
  }
  if (msg.generatedImages) return "[Delivered generated image(s).]";
  if (msg.generatedPosts) return "[Delivered generated social post(s).]";
  if (msg.generatedVideos || msg.ugcVideo) return "[Delivered generated video(s).]";
  if (msg.availability) return "[Delivered calendar availability.]";
  if (msg.booking) return "[Delivered a booked appointment.]";
  if (msg.sendDraft) return "[Delivered a reply draft, awaiting the owner's approval.]";
  if (msg.ghlContacts || msg.contactsList) return "[Delivered contact search results.]";
  if (msg.ghlConversation || msg.ghlInbox) return "[Delivered message thread results.]";
  if (msg.campaignStatus || msg.campaignReplies) return "[Delivered campaign results.]";
  if (msg.gbpProposal) return describeCard(msg.gbpProposal);

  return "";
};
